import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Button, Space, Typography, Spin, message, Avatar, Modal, Form, Input, Select, Switch } from 'antd';
import { ArrowLeftOutlined, EditOutlined, ReloadOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

const { Title } = Typography;
const { confirm } = Modal;

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  
  .ant-btn {
    background: ${COLORS.bgSecondary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    height: 40px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &:hover {
      border-color: ${COLORS.accent} !important;
      color: ${COLORS.accent} !important;
      background: ${COLORS.hover} !important;
    }
  }
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-head-title {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-descriptions-item-label {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-descriptions-item-content {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
  }
`;

const ContractAddressDetails: React.FC = () => {
  const { contractId } = useParams<{ contractId: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState(false);
  const [contractData, setContractData] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [blockchainNetworks, setBlockchainNetworks] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedBlockchainType, setSelectedBlockchainType] = useState<string | null>(null);
  const [isNativeChecked, setIsNativeChecked] = useState(false);

  useEffect(() => {
    fetchContractDetails();
    fetchCurrencies();
    fetchBlockchainNetworks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  const fetchContractDetails = async () => {
    if (!contractId) return;
    
    try {
      setLoading(true);
      const response = await apiClient.get(
        (API_ENDPOINTS.ADMIN_GET_CONTRACT_ADDRESS || '/api/Administrator/contract-address/{id}').replace('{id}', contractId)
      );
      
      if (response.data.status === 'success') {
        setContractData(response.data.data);
        // Set blockchain type for form
        if (response.data.data?.blockchainNetwork?.blockchainType) {
          setSelectedBlockchainType(response.data.data.blockchainNetwork.blockchainType);
        }
      } else {
        message.error('Failed to load contract address details');
      }
    } catch (error: any) {
      console.error('Error fetching contract address details:', error);
      message.error(error.response?.data?.message || 'Failed to load contract address details');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrencies = async () => {
    try {
      // Fetch currencies for the dropdown (max perPage is 1000)
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_CURRENCIES, {
        params: {
          pageId: 1,
          perPage: 1000, // Maximum allowed perPage value
        },
      });
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
        setCurrencies(items);
      }
    } catch (error: any) {
      console.error('Error fetching currencies:', error);
    }
  };

  const fetchBlockchainNetworks = async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_BLOCKCHAIN_NETWORKS);
      if (response.data?.status === 'success' && response.data?.data) {
        const data = response.data.data;
        const items = data.itemsList || data.items || (Array.isArray(data) ? data : []);
        setBlockchainNetworks(items);
      }
    } catch (error: any) {
      console.error('Error fetching blockchain networks:', error);
    }
  };

  const handleResyncToWatcher = async () => {
    if (!contractId) return;
    
    try {
      setResyncing(true);
      const response = await apiClient.post(
        API_ENDPOINTS.ADMIN_RESYNC_CONTRACT_ADDRESS_TO_WATCHER.replace('{id}', contractId)
      );
      
      if (response.data?.status === 'success') {
        message.success('Contract address synced to OnchainWatcher successfully');
        fetchContractDetails();
      } else {
        message.error(response.data?.message || 'Failed to sync to watcher');
      }
    } catch (error: any) {
      console.error('Error resyncing contract address to watcher:', error);
      message.error(error.response?.data?.message || 'Failed to sync to watcher');
    } finally {
      setResyncing(false);
    }
  };

  const handleEdit = () => {
    if (!contractData) return;
    
    // Set blockchain type for form
    if (contractData.blockchainNetwork?.blockchainType) {
      setSelectedBlockchainType(contractData.blockchainNetwork.blockchainType);
    }
    
    const isNative = contractData.isNative || false;
    const address = contractData.contractAddress || contractData.address || '';
    setIsNativeChecked(isNative);
    
    form.setFieldsValue({
      address: isNative ? 'native' : address,
      currencyId: contractData.currencyId,
      blockchainNetworkId: contractData.blockchainNetworkId,
      isNative: isNative,
      status: contractData.status,
      allowInOnchainWatcherMonitor: contractData.allowInOnchainWatcherMonitor ?? true,
      supportsRPCPolling: contractData.supportsRPCPolling ?? true,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload: any = {
        contractAddress: values.address,
        currencyId: values.currencyId,
        blockchainNetworkId: values.blockchainNetworkId,
        isNative: values.isNative || false,
      };

      if (values.status) {
        payload.status = values.status;
      }

      if (values.allowInOnchainWatcherMonitor !== undefined) {
        payload.allowInOnchainWatcherMonitor = values.allowInOnchainWatcherMonitor;
      }
      if (values.supportsRPCPolling !== undefined) {
        payload.supportsRPCPolling = values.supportsRPCPolling;
      }

      await apiClient.put(
        (API_ENDPOINTS.ADMIN_UPDATE_CONTRACT_ADDRESS || '/api/Administrator/contract-address/update/{id}').replace('{id}', contractId!),
        payload
      );
      message.success('Contract address updated successfully');
      setModalVisible(false);
      fetchContractDetails();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update contract address');
    }
  };

  const handleDelete = () => {
    if (!contractData) return;

    confirm({
      title: 'Delete Contract Address',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete contract address "${contractData.contractAddress || contractData.address}"? This action cannot be undone.`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await apiClient.post((API_ENDPOINTS.ADMIN_DELETE_CONTRACT_ADDRESS || '/api/Administrator/contract-address/delete/{id}').replace('{id}', contractId!));
          message.success('Contract address deleted successfully');
          navigate('/kivu/contract-addresses');
        } catch (error: any) {
          message.error(error.response?.data?.message || 'Failed to delete contract address');
        }
      },
    });
  };

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      ACTIVE: { color: '#10B981', text: 'Active' },
      PENDING: { color: '#F59E0B', text: 'Pending' },
      SUSPENDED: { color: '#EF4444', text: 'Suspended' },
      DELETED: { color: '#6B7280', text: 'Deleted' },
    };
    const config = statusConfig[status] || { color: '#6B7280', text: status };
    return (
      <Tag 
        color="transparent" 
        style={{ 
          color: config.color, 
          border: `1px solid ${config.color}40`,
          background: `${config.color}10`,
          borderRadius: '6px',
          padding: '0 8px'
        }}
      >
        {config.text}
      </Tag>
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  if (!contractData) {
    return (
      <PageContainer>
        <HeaderContainer>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
          <Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
            Contract Address Not Found
          </Title>
        </HeaderContainer>
      </PageContainer>
    );
  }

  const contractAddress = contractData.contractAddress || contractData.address;
  const explorerUrl = contractData.blockchainNetwork?.explorerUrl;
  const explorerLink = explorerUrl ? `${explorerUrl.replace(/\/$/, '')}/address/${contractAddress}` : null;

  return (
    <PageContainer>
      <HeaderContainer>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/kivu/contract-addresses')}>
            Back
          </Button>
          <Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
            Contract Address Details
          </Title>
        </Space>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleResyncToWatcher}
            loading={resyncing}
            disabled={resyncing}
          >
            Resync to Watcher
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={handleEdit}
          >
            Edit
          </Button>
          <Button
            icon={<DeleteOutlined />}
            danger
            onClick={handleDelete}
          >
            Delete
          </Button>
        </Space>
      </HeaderContainer>

      <StyledCard
        title={
          <Space>
            <div>
              <div style={{ color: COLORS.textPrimary, fontSize: '18px', fontWeight: 600 }}>
                {contractAddress ? (
                  explorerLink ? (
                    <a href={explorerLink} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent }}>
                      {contractAddress}
                    </a>
                  ) : (
                    contractAddress
                  )
                ) : (
                  'Contract Address'
                )}
              </div>
              {contractData.currency && (
                <div style={{ fontSize: '14px', color: COLORS.textSecondary, marginTop: '4px' }}>
                  {contractData.currency.symbol} - {contractData.currency.name}
                </div>
              )}
            </div>
          </Space>
        }
      >
        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
          <Descriptions.Item label="Contract Address ID">{contractData.id}</Descriptions.Item>
          <Descriptions.Item label="Watcher ID">
            {contractData.watcherId ? (
              <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{contractData.watcherId}</span>
            ) : (
              <span style={{ color: COLORS.textSecondary, fontStyle: 'italic' }}>Not synced</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Contract Address">
            {contractAddress ? (
              explorerLink ? (
                <a href={explorerLink} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent, wordBreak: 'break-all' }}>
                  {contractAddress}
                </a>
              ) : (
                <span style={{ wordBreak: 'break-all' }}>{contractAddress}</span>
              )
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Currency">
            {contractData.currency ? (
              <Space>
                {contractData.currency.iconUrl && (
                  <Avatar
                    src={contractData.currency.iconUrl}
                    size="small"
                    style={{ width: 20, height: 20 }}
                  />
                )}
                <span>{contractData.currency.symbol} - {contractData.currency.name}</span>
              </Space>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Blockchain Network">
            {contractData.blockchainNetwork ? (
              <Space>
                {contractData.blockchainNetwork.iconUrl && (
                  <Avatar
                    src={contractData.blockchainNetwork.iconUrl}
                    size="small"
                    style={{ width: 20, height: 20 }}
                  />
                )}
                <span>{contractData.blockchainNetwork.name || contractData.blockchainNetwork.abbreviation || '-'}</span>
              </Space>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Is Native">
            {contractData.isNative ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Status">{getStatusTag(contractData.status)}</Descriptions.Item>
          <Descriptions.Item label="Allow in Watcher">
            {contractData.allowInOnchainWatcherMonitor !== false ? (
              <Tag color="green">Yes</Tag>
            ) : (
              <Tag color="red">No</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Supports RPC Polling">
            {contractData.supportsRPCPolling !== false ? (
              <Tag color="green">Yes</Tag>
            ) : (
              <Tag color="red">No</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Created At">
            {contractData.createdAt ? new Date(contractData.createdAt).toLocaleString() : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {contractData.updatedAt ? new Date(contractData.updatedAt).toLocaleString() : '-'}
          </Descriptions.Item>
        </Descriptions>
      </StyledCard>

      <Modal
        title="Edit Contract Address"
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="Update"
        cancelText="Cancel"
        width={700}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary, maxHeight: '70vh', overflowY: 'auto' }}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="currencyId"
            label="Currency"
            rules={[{ required: true, message: 'Please select currency' }]}
          >
            <Select
              placeholder="Select Currency"
              showSearch
              optionFilterProp="children"
              filterOption={(input: string, option: any) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={currencies.map(currency => ({
                value: currency.id,
                label: `${currency.symbol} - ${currency.name}`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="blockchainNetworkId"
            label="Blockchain Network"
            rules={[{ required: true, message: 'Please select blockchain network' }]}
          >
            <Select
              placeholder="Select Blockchain Network"
              showSearch
              optionFilterProp="children"
              filterOption={(input: string, option: any) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              onChange={(value) => {
                const network = blockchainNetworks.find(n => n.id === value);
                setSelectedBlockchainType(network?.blockchainType || null);
              }}
              options={blockchainNetworks.map(network => ({
                value: network.id,
                label: `${network.name} (${network.abbreviation})`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="isNative"
            label="Is Native Currency"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch
              onChange={(checked) => {
                setIsNativeChecked(checked);
                if (checked) {
                  // Set address to "native" and lock it
                  form.setFieldsValue({ address: 'native' });
                } else {
                  // Clear address and unlock it
                  form.setFieldsValue({ address: '' });
                }
              }}
            />
          </Form.Item>
          <Form.Item
            name="address"
            label="Contract Address"
            rules={[{ required: true, message: 'Please enter contract address' }]}
          >
            <Input 
              placeholder={isNativeChecked ? "native" : "0x..."}
              disabled={isNativeChecked}
              value={isNativeChecked ? 'native' : undefined}
            />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            initialValue="ACTIVE"
          >
            <Select>
              <Select.Option value="ACTIVE">Active</Select.Option>
              <Select.Option value="PENDING">Pending</Select.Option>
              <Select.Option value="SUSPENDED">Suspended</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="allowInOnchainWatcherMonitor"
            label="Allow in OnchainWatcher Monitor"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="supportsRPCPolling"
            label="Supports RPC Polling"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default ContractAddressDetails;
